import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  ButtonGroup,
  Center,
  Flex,
  Icon,
  Image,
  Spinner,
  Text,
  VStack,
  keyframes,
  useColorModeValue,
} from '@chakra-ui/react';
import { GoogleMap, Marker, Circle, OverlayView } from '@react-google-maps/api';
import { MdClass, MdLocationOn, MdRadar } from 'react-icons/md';
import GoogleMapsLoader from 'components/map/GoogleMapsLoader';
import { getClassTypeIcon } from 'utils/classIcons';
import { buildOpportunityMapCameraPlan } from 'utils/opportunityMapCamera';

const mapZoomIn = keyframes`
  0% {
    transform: scale(0.3);
    opacity: 0.5;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
`;

const pulseInstructor = keyframes`
  0%, 100% {
    transform: scale(1);
    opacity: 0.8;
  }
  50% {
    transform: scale(1.4);
    opacity: 0.3;
  }
`;

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const scaleIn = keyframes`
  0% {
    transform: scale(0.8);
    opacity: 0;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
`;

const mapContainerStyle = {
  width: '100%',
  height: '100%',
};

const DEFAULT_CENTER = { lat: 34.0522, lng: -118.2437 };

export default function OpportunityFinderMap({
  instructorLocation,
  instructorName = 'Instructor Location',
  opportunities = [],
  isLoading = false,
  selectedClassId = '',
  onOpportunityClick,
  locationBadgeLabel = 'Instructor Location',
  defaultZoom = 12,
  focusZoom = 12,
  showRadiusControls = true,
  showSearchRadius = true,
  initialSearchRadius = 10,
}) {
  const [mapLoaded, setMapLoaded] = useState(false);
  const [mapInstance, setMapInstance] = useState(null);
  const [searchRadius, setSearchRadius] = useState(initialSearchRadius);
  const mapRef = useRef(null);
  const lastCameraKeyRef = useRef(null);

  const center = instructorLocation || opportunities[0]?.position || DEFAULT_CENTER;
  const searchRadiusMeters = searchRadius * 1609.34;
  const cameraPlan = buildOpportunityMapCameraPlan({
    instructorLocation,
    opportunities,
    isLoading,
    defaultZoom,
  });

  const brandColor = useColorModeValue('#4299E1', '#4299E1');
  const textColor = useColorModeValue('gray.800', 'white');
  const buttonBg = useColorModeValue('white', 'gray.700');
  const buttonBorder = useColorModeValue('gray.300', 'gray.600');
  const buttonHoverBg = useColorModeValue('gray.50', 'gray.600');
  const overlayBg = useColorModeValue('white', 'gray.800');
  const colorMode = useColorModeValue('light', 'dark');

  useEffect(() => {
    if (!mapInstance || !window.google || cameraPlan.action === 'hold' || !cameraPlan.key) return;
    if (lastCameraKeyRef.current === cameraPlan.key) return;

    lastCameraKeyRef.current = cameraPlan.key;

    if (cameraPlan.action === 'fitBounds') {
      const bounds = new window.google.maps.LatLngBounds();
      cameraPlan.points.forEach((point) => bounds.extend(point));
      mapInstance.fitBounds(bounds);
      return;
    }

    if (cameraPlan.action === 'center') {
      mapInstance.setCenter(cameraPlan.center);
      mapInstance.setZoom(focusZoom || cameraPlan.zoom);
    }
  }, [mapInstance, cameraPlan, focusZoom]);

  const onMapLoad = (map) => {
    setMapLoaded(true);
    setMapInstance(map);
    mapRef.current = map;
  };

  return (
    <Box position="relative" w="100%" h="100%" bg={brandColor}>
      <GoogleMapsLoader
        loadingElement={
          <Center h="100%">
            <VStack spacing="16px">
              <Spinner size="xl" color="white" thickness="4px" />
              <Text color="white" fontWeight="700">Loading map...</Text>
            </VStack>
          </Center>
        }
      >
        <Box
          position="relative"
          w="100%"
          h="100%"
          animation={`${mapZoomIn} 1.5s ease-out forwards`}
        >
          <GoogleMap
            mapContainerStyle={mapContainerStyle}
            center={center}
            zoom={defaultZoom}
            onLoad={onMapLoad}
            options={{
              streetViewControl: false,
              mapTypeControl: false,
              fullscreenControl: false,
              zoomControl: true,
              styles: colorMode === 'dark' ? [
                { elementType: 'geometry', stylers: [{ color: '#242f3e' }] },
                { elementType: 'labels.text.stroke', stylers: [{ color: '#242f3e' }] },
                { elementType: 'labels.text.fill', stylers: [{ color: '#746855' }] },
                { featureType: 'administrative.locality', elementType: 'labels.text.fill', stylers: [{ color: '#d59563' }] },
                { featureType: 'poi', elementType: 'labels', stylers: [{ visibility: 'off' }] },
                { featureType: 'poi.park', elementType: 'geometry', stylers: [{ color: '#263c3f' }] },
                { featureType: 'poi.park', elementType: 'labels.text.fill', stylers: [{ color: '#6b9a76' }] },
                { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#38414e' }] },
                { featureType: 'road', elementType: 'geometry.stroke', stylers: [{ color: '#212a37' }] },
                { featureType: 'road', elementType: 'labels.text.fill', stylers: [{ color: '#9ca5b3' }] },
                { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#746855' }] },
                { featureType: 'road.highway', elementType: 'geometry.stroke', stylers: [{ color: '#1f2835' }] },
                { featureType: 'road.highway', elementType: 'labels.text.fill', stylers: [{ color: '#f3d19c' }] },
                { featureType: 'transit', elementType: 'geometry', stylers: [{ color: '#2f3948' }] },
                { featureType: 'transit.station', elementType: 'labels.text.fill', stylers: [{ color: '#d59563' }] },
                { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#17263c' }] },
                { featureType: 'water', elementType: 'labels.text.fill', stylers: [{ color: '#515c6d' }] },
                { featureType: 'water', elementType: 'labels.text.stroke', stylers: [{ color: '#17263c' }] },
              ] : [
                { featureType: 'poi', elementType: 'labels', stylers: [{ visibility: 'off' }] },
              ],
            }}
          >
            {mapLoaded && window.google && (
              <>
                {instructorLocation && (
                  <>
                    <Marker
                      position={instructorLocation}
                      icon={{
                        path: 'M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z',
                        fillColor: '#48BB78',
                        fillOpacity: 1,
                        strokeColor: 'white',
                        strokeWeight: 3,
                        scale: 2.5,
                        anchor: new window.google.maps.Point(12, 22),
                      }}
                      title={instructorName}
                      zIndex={1000}
                    />

                    <Circle
                      center={instructorLocation}
                      radius={300}
                      options={{
                        fillColor: '#48BB78',
                        fillOpacity: 0.15,
                        strokeColor: '#48BB78',
                        strokeOpacity: 0.8,
                        strokeWeight: 3,
                      }}
                    />

                    {showSearchRadius && (
                      <Circle
                        center={instructorLocation}
                        radius={searchRadiusMeters}
                        options={{
                          fillColor: brandColor,
                          fillOpacity: 0.05,
                          strokeColor: brandColor,
                          strokeOpacity: 0.3,
                          strokeWeight: 2,
                        }}
                      />
                    )}
                  </>
                )}

                {!isLoading && opportunities.map((opportunity) => {
                  const isSelected = selectedClassId === opportunity.id;
                  const opportunityIcon = getClassTypeIcon(
                    opportunity.classData?.ClassName || opportunity.classData?.ClassType || ''
                  );

                  return (
                    <OverlayView
                      key={opportunity.id}
                      position={opportunity.position}
                      mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                    >
                      <Flex
                        data-testid="opportunity-map-class-pin"
                        title={opportunity.facilityName}
                        role="button"
                        tabIndex={0}
                        aria-label={`Open ${opportunity.facilityName} class opportunity`}
                        onClick={() => onOpportunityClick?.(opportunity.classData)}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            onOpportunityClick?.(opportunity.classData);
                          }
                        }}
                        w={isSelected ? '54px' : '48px'}
                        h={isSelected ? '64px' : '58px'}
                        align="center"
                        justify="flex-start"
                        direction="column"
                        cursor="pointer"
                        transform="translate(-50%, -100%)"
                        transition="transform 0.18s ease"
                        zIndex={isSelected ? 900 : 800}
                        _hover={{ transform: 'translate(-50%, -100%) scale(1.08)' }}
                      >
                        <Flex
                          w={isSelected ? '48px' : '42px'}
                          h={isSelected ? '48px' : '42px'}
                          borderRadius="16px"
                          bg={isSelected ? '#2B84C4' : '#2B6CB0'}
                          align="center"
                          justify="center"
                          border="3px solid white"
                          boxShadow="0 8px 18px rgba(43, 108, 176, 0.35)"
                        >
                          <Flex
                            w={isSelected ? '34px' : '30px'}
                            h={isSelected ? '34px' : '30px'}
                            borderRadius="full"
                            bg="white"
                            align="center"
                            justify="center"
                          >
                            {opportunityIcon ? (
                              <Image
                                src={opportunityIcon}
                                alt=""
                                w={isSelected ? '25px' : '22px'}
                                h={isSelected ? '25px' : '22px'}
                                objectFit="contain"
                              />
                            ) : (
                              <Icon as={MdClass} color={isSelected ? '#2B84C4' : '#2B6CB0'} w="20px" h="20px" />
                            )}
                          </Flex>
                        </Flex>
                        <Box
                          w="0"
                          h="0"
                          borderLeft="9px solid transparent"
                          borderRight="9px solid transparent"
                          borderTop={`12px solid ${isSelected ? '#2B84C4' : '#2B6CB0'}`}
                          mt="-2px"
                          filter="drop-shadow(0 4px 5px rgba(43, 108, 176, 0.25))"
                        />
                      </Flex>
                    </OverlayView>
                  );
                })}
              </>
            )}
          </GoogleMap>

          {isLoading && (
            <Flex
              position="absolute"
              top="50%"
              left="50%"
              transform="translate(-50%, -50%)"
              direction="column"
              align="center"
              gap="12px"
              pointerEvents="none"
              zIndex="10"
            >
              <Box animation={`${pulseInstructor} 2s ease-in-out infinite`}>
                <Icon as={MdLocationOn} w="48px" h="48px" color="white" filter="drop-shadow(0 4px 8px rgba(0,0,0,0.5))" />
              </Box>
              <Box animation={`${fadeIn} 0.5s ease-out 0.5s forwards`} opacity="0">
                <Text
                  fontSize="sm"
                  fontWeight="700"
                  color="white"
                  textAlign="center"
                  textShadow="0 2px 8px rgba(0,0,0,0.5)"
                >
                  Finding nearby classes...
                </Text>
              </Box>
            </Flex>
          )}

          {!isLoading && (
            <Box
              position="absolute"
              top="16px"
              left="50%"
              transform="translateX(-50%)"
              zIndex="5"
              animation={`${fadeIn} 0.6s ease-out forwards`}
            >
              <Flex
                align="center"
                gap="6px"
                bg="green.500"
                px="14px"
                py="8px"
                borderRadius="full"
                boxShadow="0 3px 12px rgba(72, 187, 120, 0.4)"
              >
                <Icon as={MdLocationOn} w="18px" h="18px" color="white" />
                <Text fontSize="sm" fontWeight="700" color="white" letterSpacing="wide">
                  {locationBadgeLabel}
                </Text>
              </Flex>
            </Box>
          )}

          {!isLoading && showRadiusControls && (
            <Box
              position="absolute"
              top="16px"
              right="16px"
              zIndex="5"
              animation={`${scaleIn} 0.4s cubic-bezier(0.34, 1.56, 0.64, 1) forwards`}
            >
              <VStack spacing="8px" align="stretch">
                <Flex
                  align="center"
                  justify="center"
                  gap="4px"
                  bg="white"
                  px="10px"
                  py="6px"
                  borderRadius="md"
                  boxShadow="0 2px 8px rgba(0,0,0,0.15)"
                >
                  <Icon as={MdRadar} w="14px" h="14px" color="brand.500" />
                  <Text
                    fontSize="xs"
                    fontWeight="700"
                    color="gray.700"
                    textTransform="uppercase"
                    letterSpacing="wider"
                  >
                    Search Radius
                  </Text>
                </Flex>
                <ButtonGroup isAttached size="md" variant="outline" boxShadow="0 4px 12px rgba(0,0,0,0.2)">
                  {[10, 25, 50].map((radius) => (
                    <Button
                      key={radius}
                      onClick={() => setSearchRadius(radius)}
                      bg={searchRadius === radius ? 'brand.500' : buttonBg}
                      color={searchRadius === radius ? 'white' : textColor}
                      fontWeight="700"
                      fontSize="sm"
                      borderColor={searchRadius === radius ? 'brand.500' : buttonBorder}
                      boxShadow={searchRadius === radius ? '0 0 0 3px rgba(66, 153, 225, 0.3)' : 'none'}
                      _hover={{
                        bg: searchRadius === radius ? 'brand.600' : buttonHoverBg,
                        borderColor: searchRadius === radius ? 'brand.600' : 'brand.400',
                        transform: 'scale(1.05)',
                      }}
                      _active={{ transform: 'scale(0.98)' }}
                      transition="all 0.2s"
                    >
                      {radius} mi
                    </Button>
                  ))}
                </ButtonGroup>
              </VStack>
            </Box>
          )}

          {!isLoading && opportunities.length > 0 && (
            <Box
              position="absolute"
              bottom="16px"
              left="50%"
              transform="translateX(-50%)"
              bg={overlayBg}
              px="16px"
              py="8px"
              borderRadius="full"
              boxShadow="0 2px 12px rgba(0,0,0,0.2)"
              animation={`${fadeIn} 0.5s ease-out forwards`}
            >
              <Flex align="center" gap="6px">
                <Icon as={MdClass} color="brand.500" />
                <Text fontSize="sm" fontWeight="700" color={textColor}>
                  {opportunities.length} opportunit{opportunities.length === 1 ? 'y' : 'ies'} found
                </Text>
              </Flex>
            </Box>
          )}
        </Box>
      </GoogleMapsLoader>
    </Box>
  );
}
